import React, { useMemo } from 'react';

import { Time } from '@functions/index';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';

import t from '@localise/translate';

import { Unavailability } from '@pages/Admin/Unavailability';

import styles from './style.scss';

type Props = {
  onClick?: () => void;
  unavailability: Unavailability[] | null;
};

export const TextOpenClose = ({ onClick, unavailability }: Props) => {
  const dayOpening = useMemo(() => {
    const date = new Date();
    const day = date.getDay();

    const isOpenTextVisible = t('nav.openCloseTextVisible');
    const openingHours = t('openingHours') as Record<string, string>;

    if (!openingHours || !isOpenTextVisible) {
      return null;
    }

    const hours = openingHours[day + 1];

    if (!hours) {
      return null;
    }

    const openFrom = hours.split(' - ')[0];
    const openTo = hours.split(' - ')[1];
    const currentTime = date.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' });
    const isOpen = currentTime >= openFrom && currentTime <= openTo;

    const isTodayUnavailable = unavailability?.some((unavailability) => {
      const unavailabilityStart = new Date(unavailability.sinceDate);
      const unavailabilityEnd = new Date(unavailability.untilDate);
      return date >= unavailabilityStart && date <= unavailabilityEnd;
    });

    if (isOpen && !isTodayUnavailable) {
      return { dot: true, active: true, text: t('nav.openText', { time: openTo }) };
    }

    const tomorrow = new Date(date).setDate(date.getDate() + 1);
    const tomorrowDay = new Date(tomorrow).getDay();
    const tomorrowHours = openingHours[tomorrowDay]?.includes('-') ? openingHours[tomorrowDay] : null;

    const isTomorrowUnavailable = unavailability?.some((unavailability) => {
      const unavailabilityStart = new Date(unavailability.sinceDate);
      const unavailabilityEnd = new Date(unavailability.untilDate);

      return new Date(tomorrow) >= unavailabilityStart && new Date(tomorrow) <= unavailabilityEnd;
    });

    if (tomorrowHours && !isTomorrowUnavailable) {
      const nextDay = new Date(date).setDate(date.getDate() + 1);
      const nextDayDate = new Date(nextDay);
      const nextDayFormatted = nextDayDate.toLocaleDateString('sk-SK', { weekday: 'long' });
      const nextDayOpenFrom = tomorrowHours.split(' - ')[0];

      return { dot: false, active: true, text: t('nav.openTomorrowText', { day: nextDayFormatted, time: nextDayOpenFrom }) };
    }

    const nextDays = Array.from({ length: 21 }, (_, i) => {
      const nextDay = new Date(date).setDate(date.getDate() + i + 2);
      const nextDayDay = new Date(nextDay).getDay();
      const nextDayHours = openingHours[nextDayDay]?.includes('-') ? openingHours[nextDayDay] : null;

      return { nextDay, nextDayHours };
    });

    const firstPossibleDate = nextDays.find(({ nextDay, nextDayHours }) => {
      const isNextDayUnavailable = unavailability?.some((unavailability) => {
        const unavailabilityStart = new Date(unavailability.sinceDate);
        const unavailabilityEnd = new Date(unavailability.untilDate);
        return new Date(nextDay) >= unavailabilityStart && new Date(nextDay) <= unavailabilityEnd;
      });

      return nextDayHours?.includes('-') && !isNextDayUnavailable;
    });

    if (firstPossibleDate) {
      const firstPossibleDateDay = t(`contact.openingHoursDays.${new Date(firstPossibleDate.nextDay).getDay()}`);
      const firstPossibleDateFormatted = Time.formatDay(new Date(firstPossibleDate.nextDay));

      const isIn5Days = new Date(firstPossibleDate.nextDay).getDate() < new Date(date).getDate() + 5;

      return {
        dot: false,
        text: t('nav.openFromExtendedText', {
          day: isIn5Days ? firstPossibleDateDay : firstPossibleDateFormatted,
          time: firstPossibleDate.nextDayHours!.split(' - ')[0],
        }),
      };
    }

    return null;
  }, [!!unavailability]);

  const currentUnavailability = unavailability?.find((unavailability) => {
    const date = new Date();
    const unavailabilityStart = new Date(unavailability.sinceDate);
    const unavailabilityEnd = new Date(unavailability.untilDate);
    return date >= unavailabilityStart && date <= unavailabilityEnd;
  });

  if (!dayOpening) {
    return null;
  }

  return (
    <ButtonBase
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 20px',
        borderRadius: '8px',
        gap: '8px',
      }}
      onClick={onClick}
    >
      {dayOpening.dot && <div className={styles.activePoint} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        {currentUnavailability?.text ? (
          <Typography variant="body1" className={styles.textInactive}>
            {currentUnavailability?.text}
          </Typography>
        ) : (
          <Typography variant="body1" className={dayOpening.active ? styles.textActive : styles.textInactive}>
            {dayOpening.text}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
};
